import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PriorityBadge, GateBadge, SegmentBadge, ScoreBar, fmtDate, VerifiedTriggerBadge } from "@/lib/radar";
import { Search, Plus, BadgeCheck, Download, Info, Trash2, RotateCcw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TriggerLabel } from "@/lib/triggers";
import { toast } from "sonner";

const PRIORITIES = ["P1", "P2", "P3", "P4", "Exclude"];
const GATES = ["Review Ready", "Validate Data", "Research Required", "Research", "Nurture", "Established Account Pathway", "Do Not Contact"];

export default function Companies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("all");
  const [gate, setGate] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", website: "", abn: "", acn: "", city: "", state: "", icn_reference_url: "", analyst_note: "" });

  const canAdd = user && ["admin", "analyst"].includes(user.role);
  const canExport = user && ["admin", "analyst"].includes(user.role);
  const canDelete = user?.role === "admin";
  const [confirm, setConfirm] = useState<any>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archived, setArchived] = useState<any[]>([]);
  const openArchived = async () => {
    try { const { data } = await api.get("/companies/archived"); setArchived(data); setArchivedOpen(true); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const restore = async (c: any) => {
    try { await api.post(`/companies/${c.id}/restore`); toast.success(`${c.company_name} restored`); openArchived(); load(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = () => {
    const params: any = {};
    if (q) params.q = q;
    if (priority !== "all") params.priority = priority;
    if (gate !== "all") params.action_gate = gate;
    api.get("/companies", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, priority, gate]);

  const submitAdd = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    try {
      const { data } = await api.post("/companies/add-to-radar", form);
      toast.success(data.message);
      setAddOpen(false);
      setForm({ company_name: "", website: "", abn: "", acn: "", city: "", state: "", icn_reference_url: "", analyst_note: "" });
      load();
      navigate(`/companies/${data.id}`);
    } catch (e: any) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doExport = async () => {
    try {
      const res = await api.get("/companies/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "radar_export.csv"; a.click();
      toast.success("Export generated (logged to audit)");
    } catch (e: any) {
      toast.error(apiError(e.response?.data?.detail) || "Export not permitted");
    }
  };

  const archiveOne = async (c: any) => {
    try { await api.delete(`/companies/${c.id}`); toast.success(`${c.company_name} archived`); setConfirm(null); load(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const purgeSynthetic = async () => {
    try {
      const { data } = await api.post("/companies/purge-synthetic");
      toast.success(`${data.archived} synthetic compan${data.archived === 1 ? "y" : "ies"} archived`);
      setConfirm(null); load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const synthCount = rows.filter((r) => r.is_synthetic).length;

  return (
    <div className="space-y-4" data-testid="companies-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Company List</h1>
          <p className="text-sm text-slate-500">{rows.length} organisations · sorted by Radar Opportunity Score</p>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button variant="outline" size="sm" onClick={openArchived} data-testid="archived-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Archived
            </Button>
          )}
          {canDelete && synthCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "bulk", count: synthCount })}
              data-testid="purge-synthetic-btn" className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1" /> Remove synthetic data ({synthCount})
            </Button>
          )}
          {canExport && (
            <Button variant="outline" size="sm" onClick={doExport} data-testid="export-btn">
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
          )}
          {canAdd && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#0B1C3D] hover:bg-[#122a55]" data-testid="add-company-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Company to Radar
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-company-dialog">
                <DialogHeader>
                  <DialogTitle>Add Company to Radar</DialogTitle>
                  <DialogDescription>
                    Manual candidate entry only. Enrichment is queued but all automated connectors remain disabled in Phase 01.
                    ICN reference is analyst-recorded context — no automated ICN access.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Company name *</Label>
                    <Input data-testid="add-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input data-testid="add-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>ABN</Label>
                      <Input data-testid="add-abn" value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
                    </div>
                    <div>
                      <Label>ACN</Label>
                      <Input data-testid="add-acn" value={form.acn} onChange={(e) => setForm({ ...form, acn: e.target.value })} />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input data-testid="add-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    </div>
                    <div>
                      <Label>State / Territory</Label>
                      <Select value={form.state || "__none__"} onValueChange={(v) => setForm({ ...form, state: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="add-state"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>ICN reference URL (optional)</Label>
                    <Input data-testid="add-icn" value={form.icn_reference_url} onChange={(e) => setForm({ ...form, icn_reference_url: e.target.value })} placeholder="Manual reference only" />
                  </div>
                  <div>
                    <Label>Analyst note</Label>
                    <Textarea data-testid="add-note" value={form.analyst_note} onChange={(e) => setForm({ ...form, analyst_note: e.target.value })}
                      placeholder="Do not enter PROTECTED/classified material, clearance or FOCI data." />
                  </div>
                  <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    Unclassified internal system. Never store PROTECTED/classified, clearance or detailed FOCI material.
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submitAdd} data-testid="add-submit">Create candidate</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company…" className="pl-8" />
        </div>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-[150px]" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={gate} onValueChange={setGate}>
          <SelectTrigger className="w-[200px]" data-testid="filter-gate"><SelectValue placeholder="Action gate" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All action gates</SelectItem>
            {GATES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* table */}
      <Card className="p-0 border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left font-medium px-4 py-2.5">Company</th>
                <th className="text-right font-medium px-3 py-2.5">Opp. Score</th>
                <th className="text-right font-medium px-3 py-2.5">Evidence Conf.</th>
                <th className="text-right font-medium px-3 py-2.5">Why Now</th>
                <th className="text-right font-medium px-3 py-2.5">Emp.</th>
                <th className="text-left font-medium px-3 py-2.5">Sector</th>
                <th className="text-left font-medium px-3 py-2.5">Primary Trigger</th>
                <th className="text-left font-medium px-3 py-2.5">Priority</th>
                <th className="text-left font-medium px-3 py-2.5">Action Gate</th>
                <th className="text-right font-medium px-3 py-2.5">Last Scanned</th>
                {canDelete && <th className="text-right font-medium px-3 py-2.5">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`} onClick={() => navigate(`/companies/${c.id}`)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-900">{c.company_name}</span>
                      {c.verified && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span data-testid={`verified-${c.id}`} className="inline-flex cursor-help" onClick={(e) => e.stopPropagation()}>
                                <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-slate-900 text-slate-50 leading-relaxed" data-testid={`verified-${c.id}-tooltip`}>
                              Analyst-verified — a reviewer has manually confirmed or overridden this record's facts (via Verify / Override).
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {c.p0_trigger && <VerifiedTriggerBadge compact testid={`trigger-${c.id}`} />}
                      {c.is_synthetic && (
                        <span data-testid={`synthetic-${c.id}`} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">Synthetic</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">{c.state || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5"><ScoreBar value={c.opportunity_score} /></td>
                  <td className="px-3 py-2.5"><ScoreBar value={c.evidence_confidence} kind="confidence" /></td>
                  <td className="px-3 py-2.5 text-right num text-slate-700">{c.why_now}</td>
                  <td className="px-3 py-2.5 text-right num text-slate-700">{c.employee_estimate ?? "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{c.industry || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">
                    <TriggerLabel value={c.primary_trigger} testid={`trigger-cell-${c.id}`} />
                  </td>
                  <td className="px-3 py-2.5"><PriorityBadge value={c.priority} /></td>
                  <td className="px-3 py-2.5"><GateBadge value={c.action_gate} /></td>
                  <td className="px-3 py-2.5 text-right num text-[11px] text-slate-400">{fmtDate(c.last_scanned_at)}</td>
                  {canDelete && (
                    <td className="px-3 py-2.5 text-right">
                      <button data-testid={`delete-${c.id}`} onClick={(e) => { e.stopPropagation(); setConfirm({ type: "one", company: c }); }}
                        className="text-slate-400 hover:text-red-600 transition-colors" title="Archive company">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={canDelete ? 11 : 10} className="px-4 py-10 text-center text-slate-400">No companies match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Archive confirmation */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="confirm-archive-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.type === "bulk" ? "Remove all synthetic seed companies?" : "Archive company?"}</DialogTitle>
            <DialogDescription>
              {confirm?.type === "bulk"
                ? `This archives ${confirm?.count} synthetic seed compan${confirm?.count === 1 ? "y" : "ies"}. They are hidden from every view but kept for audit and can be restored by an admin. Real companies you have added are not affected.`
                : `This archives "${confirm?.company?.company_name}". It is hidden from every view but kept for audit and can be restored by an admin.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="confirm-archive-btn"
              onClick={() => (confirm?.type === "bulk" ? purgeSynthetic() : archiveOne(confirm.company))}>
              {confirm?.type === "bulk" ? `Archive ${confirm?.count}` : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived companies */}
      <Dialog open={archivedOpen} onOpenChange={setArchivedOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="archived-dialog">
          <DialogHeader>
            <DialogTitle>Archived companies</DialogTitle>
            <DialogDescription>Soft-deleted companies are hidden from all views but kept for audit. Restore any to bring it back to the Radar.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-slate-100">
            {archived.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No archived companies.</div>}
            {archived.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2 text-sm gap-2" data-testid={`archived-row-${c.id}`}>
                <div className="min-w-0">
                  <span className="font-medium text-slate-800">{c.company_name}</span>
                  {c.is_synthetic && <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">Synthetic</span>}
                  <span className="text-slate-400 ml-2">{c.industry || ""}</span>
                  {c.deleted_at && <span className="text-[11px] text-slate-400 ml-2">· archived {fmtDate(c.deleted_at)}</span>}
                </div>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => restore(c)} data-testid={`restore-${c.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
